import { useState, useEffect, useRef } from "react";
import { Box, Typography, Avatar, Chip, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  AiOutlinePlayCircle,
  AiOutlineShareAlt,
  AiOutlineComment,
} from "react-icons/ai";
import { IoVolumeMute, IoVolumeHigh } from "react-icons/io5";
import { BiExpand, BiCollapse } from "react-icons/bi";
import { MdAccessTime, MdPerson } from "react-icons/md";
import { BookmarkRemove, StarRounded } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { Client, Databases, Query } from "appwrite";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button 
} from "@mui/material";
import { Warning, DeleteOutline } from "@mui/icons-material";

const SavedVideos = () => {
  const [savedVideos, setSavedVideos] = useState([]);
  const navigate = useNavigate();
  const [videoStates, setVideoStates] = useState({});
  const videoRefs = useRef({});
  const videoContainerRefs = useRef({});
  const progressBarRefs = useRef({});
  
  // Add theme for responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  
  // Get current user ID from Redux store
  const user = useSelector((state) => state.user);
  const currentUserId = user?._id;
  
  // Get user ID from URL params
  const { userId } = useParams();
  // 2. Add these state variables inside your SavedVideos component
const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
const [videoToDelete, setVideoToDelete] = useState(null);


// 3. Add these functions to handle the dialog
const openDeleteDialog = (videoId, e) => {
  e.stopPropagation();
  setVideoToDelete(videoId);
  setDeleteDialogOpen(true);
};

const closeDeleteDialog = () => {
  setDeleteDialogOpen(false);
  setVideoToDelete(null);
};

const confirmDelete = async () => {
  if (videoToDelete) {
    await handleRemoveVideo(videoToDelete);
    closeDeleteDialog();
  }
};
  
  // Check if the user is viewing their own saved videos
  const isOwnSavedVideos = currentUserId === userId;

  const client = new Client();
  client
    .setEndpoint(process.env.REACT_APP_APPWRITE_ENDPOINT)
    .setProject(process.env.REACT_APP_APPWRITE_PROJECT_ID);

  const databases = new Databases(client);

  // Constants
const DATABASE_ID = "681f7caf0033c79e0bc0";
  const SAVED_VIDEOS_COLLECTION_ID = "681f7f8800028b55473e";


  // Format time to MM:SS or HH:MM:SS
  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === Infinity) return "00:00";
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Load saved videos from Appwrite and filter by user ID
  useEffect(() => {
    // Redirect if trying to access someone else's saved videos
    if (!isOwnSavedVideos && userId !== currentUserId) {
      navigate("/home");
      return;
    }
    
    const fetchSavedVideos = async () => {
      try {
        // Fetch videos saved by the current user
        const response = await databases.listDocuments(
          DATABASE_ID,
          SAVED_VIDEOS_COLLECTION_ID,
          [Query.equal('userId', currentUserId)]
        );
        
        // Transform Appwrite data to match your component's expected format
        const userVideos = response.documents.map(doc => ({
          id: doc.postId,
          title: doc.title || "Untitled Video",
          thumbnail: doc.thumbnail,
          creator: doc.creator || "User",
          talent: doc.talent || "Talent",
          profilePic: doc.userImage,
          userId: doc.postOwnerId,
          savedBy: doc.userId,
          createdAt: doc.savedAt,
          hashtags: doc.hashtags || []
        }));
        
        setSavedVideos(userVideos);
        
        // Initialize video states
        const initialStates = userVideos.reduce((acc, video) => {
          acc[video.id] = {
            playing: false,
            muted: true,
            isFullscreen: false,
            progress: 0,
            duration: 0,
            currentTime: 0
          };
          return acc;
        }, {});
        
        setVideoStates(initialStates);
      } catch (error) {
        // console.error("Error fetching saved videos from Appwrite:", error);
        setSavedVideos([]);
      }
    };

    if (currentUserId) {
      fetchSavedVideos();
    }
  }, [currentUserId, isOwnSavedVideos, navigate, userId]);

  // Match the fullscreen change event listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        // Reset all fullscreen states when exiting
        setVideoStates(prev => {
          const newStates = {...prev};
          Object.keys(newStates).forEach(id => {
            newStates[id] = {...newStates[id], isFullscreen: false};
          });
          return newStates;
        });
      }
    };
    
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Check if a video belongs to the current user
  const isOwnVideo = (video) => {
    return video.userId === currentUserId;
  };

  // Remove a video from saved list
  const handleRemoveVideo = async (videoId) => {
    try {
      // Find the saved document for this video and user
      const response = await databases.listDocuments(
        DATABASE_ID,
        SAVED_VIDEOS_COLLECTION_ID,
        [
          Query.equal('userId', currentUserId),
          Query.equal('postId', videoId)
        ]
      );
      
      if (response.documents.length > 0) {
        // Get the document ID and delete it
        const savedDocId = response.documents[0].$id;
        await databases.deleteDocument(
          DATABASE_ID,
          SAVED_VIDEOS_COLLECTION_ID,
          savedDocId
        );
        
        // Update state to reflect changes
        setSavedVideos(savedVideos.filter(video => video.id !== videoId));
        
        // Show success message (you can add toast notification here)
        // console.log("Video removed from saved collection");
      }
    } catch (error) {
      // console.error("Error removing video from saved collection:", error);
      // Show error message (you can add toast notification here)
    }
  };

  // Navigate to the post when clicked - IMPROVED VERSION
  // const handleVideoClick = (videoId) => {
  //   // Stop any playing videos before navigating
  //   Object.keys(videoRefs.current).forEach(id => {
  //     if (videoRefs.current[id]) {
  //       videoRefs.current[id].pause();
  //     }
  //   });
    
  //   // Navigate to the specific post page
  //   navigate(`/home/post/${videoId}`);
  // };

    const handleVideoClick = (videoId) => {
    navigate(`/home/post/${videoId}?source=shared`);
  };

  // Video playback controls
 // Also update the handlePlayPause function to properly handle the events
const handlePlayPause = (videoId, e) => {
  if (e) {
    e.stopPropagation(); // Stop event from reaching the container
  }
  
  const videoElement = videoRefs.current[videoId];
  
  if (!videoElement) return;
  
  if (videoElement.paused) {
    // Pause all other videos first
    Object.keys(videoRefs.current).forEach((id) => {
      if (id !== videoId && videoRefs.current[id]) {
        videoRefs.current[id].pause();
      }
    });
    
    videoElement.play();
  } else {
    videoElement.pause();
  }
}

  // MODIFIED: Toggle mute function that now applies to all videos
  const toggleMute = (videoId, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    // Get current muted state from the clicked video
    const currentMutedState = videoStates[videoId]?.muted;
    const newMutedState = !currentMutedState;
    
    // Apply the new muted state to all videos
    Object.keys(videoRefs.current).forEach(id => {
      if (videoRefs.current[id]) {
        videoRefs.current[id].muted = newMutedState;
      }
    });
    
    // Update all video states at once
    setVideoStates(prev => {
      const newStates = {...prev};
      Object.keys(newStates).forEach(id => {
        newStates[id] = {...newStates[id], muted: newMutedState};
      });
      return newStates;
    });
    
    // Show a small toast notification (optional)
    const message = newMutedState ? "All videos muted" : "All videos unmuted";
    // console.log(message);
    // You could add a toast notification here if you have a toast library
  };

  // Fullscreen implementation
  const toggleFullscreen = (videoId, e) => {
    if (e) {
      e.stopPropagation();
    }
    
    if (!videoContainerRefs.current[videoId]) return;
    
    const el = videoContainerRefs.current[videoId];
    const isCurrentlyFullscreen = videoStates[videoId]?.isFullscreen || false;
    
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().then(() => {
        setVideoStates(prev => ({
          ...prev,
          [videoId]: { ...prev[videoId], isFullscreen: true }
        }));
      }).catch(err => {
        // console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen?.().then(() => {
        setVideoStates(prev => ({
          ...prev,
          [videoId]: { ...prev[videoId], isFullscreen: false }
        }));
      }).catch(err => {
        // console.error(`Error attempting to exit fullscreen: ${err.message}`);
      });
    }
  };

  // Update progress as video plays
  const handleTimeUpdate = (videoId) => {
    if (!videoRefs.current[videoId]) return;
    
    const current = videoRefs.current[videoId].currentTime || 0;
    const duration = videoRefs.current[videoId].duration || 1;
    const progress = (current / duration) * 100;
    
    setVideoStates(prev => ({
      ...prev,
      [videoId]: { 
        ...prev[videoId], 
        progress,
        currentTime: current
      }
    }));
  };

  // Seek video when user clicks on the progress bar
  const handleProgressClick = (e, videoId) => {
    e.stopPropagation(); // Prevent navigation when clicking on progress bar
    
    if (!progressBarRefs.current[videoId] || !videoRefs.current[videoId]) return;
    
    const bar = progressBarRefs.current[videoId];
    const videoElement = videoRefs.current[videoId];
    
    const rect = bar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;

    const percentage = clickX / width;
    const newTime = videoElement.duration * percentage;
    videoElement.currentTime = newTime;
  };

  // Handler for when video metadata loads
  const handleMetadataLoaded = (videoId) => {
    if (!videoRefs.current[videoId]) return;
    
    const videoElement = videoRefs.current[videoId];
    const duration = videoElement.duration || 0;
    
    setVideoStates(prev => ({
      ...prev,
      [videoId]: { ...prev[videoId], duration }
    }));
  };

  // Navigate to user profile
  const navigateToProfile = (userId, e) => {
    e.stopPropagation();
    if (userId) {
      navigate(`/home/profile/${userId}`);
    }
  };

  // Get video card width based on screen size
  const getVideoCardWidth = () => {
    if (isMobile) return "100%";
    if (isTablet) return "45%";
    if (isLargeScreen) return "23%";
    return "30%"; // Default (medium screens)
  };

  return (
    <Box sx={{ 
      overflowX: "hidden", 
      backgroundColor: "white",
      minHeight: "100vh"
    }}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          // CHANGE: Reduced top padding to move content upward
          pt: { xs: 2, sm: 3, md: 4 },
          pb: { xs: 4, sm: 6, md: 8 },
          px: { xs: 2, sm: 3, md: 4 },
          minHeight: "100vh",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 2, sm: 2, md: 3 }, // Reduced gap to compact elements
          marginLeft: { xs: 0, sm: 0, md: "130px" },
          transition: "all 0.3s ease",
          backgroundColor: "#ffffff",
          backgroundImage: "radial-gradient(#f0f0f0 1px, transparent 1px)",
          backgroundSize: "20px 20px"
        }}
      >
        {/* CHANGE: Moved heading up and reduced margins */}
        <Box sx={{ 
          textAlign: "center", 
          mb: { xs: 1, sm: 2, md: 2 }, // Reduced margin to move content up
          width: "100%",
          maxWidth: "1200px",
          mt: { xs: 0, sm: 0, md: 0 } // Removed top margin
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
              color: "#333",
              mb: 1,
              position: "relative",
              display: "inline-block"
            }}
          >
            <Box sx={{
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: "-8px",
                left: "50%",
                transform: "translateX(-50%)",
                width: "60px",
                height: "4px",
                background: "linear-gradient(to right, #F02C56, #8B5CF6)",
                borderRadius: "2px",
              }
            }}>
              📌 Your Saved Videos
            </Box>
          </Typography>
        </Box>
        
        {savedVideos.length === 0 ? (
          <Box sx={{ 
            textAlign: "center", 
            py: 8, 
            px: 2, 
            borderRadius: 4,
            bgcolor: "rgba(0,0,0,0.02)",
            border: "1px dashed #ddd",
            width: "100%",
            maxWidth: "600px"
          }}>
            <Typography variant="h6" sx={{ color: "#555", mb: 2 }}>
              No saved videos yet
            </Typography>
            <Typography variant="body2" sx={{ color: "#777" }}>
              Start saving videos to build your collection and watch them anytime
            </Typography>
          </Box>
        ) : (
          <Box 
            sx={{ 
              display: "flex", 
              flexWrap: "wrap",
              justifyContent: { xs: "center", sm: "center", md: "flex-start" },
              gap: { xs: 2, sm: 2.5, md: 3 }, // Slightly reduced gap
              width: "100%",
              maxWidth: "1400px",
              px: { xs: 1, sm: 2, md: 3 },
              mt: { xs: 1, sm: 1, md: 1 } // Added small top margin after header
            }}
          >
            {savedVideos.map((video) => (
              <Box
                key={video.id}
                sx={{
                  bgcolor: "white",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                  borderRadius: "16px",
                  width: getVideoCardWidth(),
                  minWidth: { xs: "240px", sm: "260px", md: "280px" },
                  maxWidth: { xs: "100%", sm: "280px", md: "300px" },
                  p: { xs: 1.5, sm: 2, md: 2.5 },
                  position: "relative",
                  transition: "all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1)",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 16px 32px rgba(0,0,0,0.12)",
                  },
                  mb: { xs: 2, sm: 3 }, // Reduced bottom margin
                  border: isOwnVideo(video) ? "2px solid #8B5CF6" : "2px solid transparent",
                  cursor: "pointer", 
                }}
                onClick={() => handleVideoClick(video.id)}
              >
                {/* User's Own Video Indicator */}
                {isOwnVideo(video) && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -12,
                      left: 20,
                      zIndex: 5,
                    }}
                  >
                    <Chip
                      icon={<StarRounded />}
                      label="Your Creation"
                      size="small"
                      sx={{
                        bgcolor: "#8B5CF6",
                        color: "white",
                        fontWeight: "bold",
                        boxShadow: "0 4px 8px rgba(139, 92, 246, 0.3)",
                        "& .MuiChip-icon": {
                          color: "#FFD700"
                        }
                      }}
                    />
                  </Box>
                )}

                {/* User Info Section */}
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 1.5 }}> {/* Reduced margin */}
                  {/* Profile Picture */}
                  <Avatar
                    src={video.profilePic || "https://via.placeholder.com/40"}
                    alt={video.creator}
                    sx={{ 
                      width: { xs: 36, sm: 40 }, 
                      height: { xs: 36, sm: 40 }, 
                      border: "2px solid",
                      cursor: "pointer",
                      borderColor: isOwnVideo(video) ? "#8B5CF6" : "#e0e0e0",
                      transition: "transform 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.1)"
                      }
                    }}
                    onClick={(e) => navigateToProfile(video.userId, e)}
                  />
                  
                  {/* Username and Subtitle */}
                  <Box sx={{ flex: 1, overflow: "hidden" }}>
                    <Typography 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: { xs: 13, sm: 14 }, 
                        color: "#1a1a1a",
                        cursor: "pointer",
                        "&:hover": { color: "#F02C56" },
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      onClick={(e) => navigateToProfile(video.userId, e)}
                    >
                      {video.creator} {isOwnVideo(video) && 
                        <Box component="span" sx={{ 
                          color: "#8B5CF6", 
                          fontStyle: "italic",
                          ml: 0.5 
                        }}>
                          (You)
                        </Box>
                      }
                    </Typography>
                  </Box>
                  
                  {/* Talent Badge - Category Label */}
                  <Box>
                    <Typography 
                      sx={{ 
                        fontSize: { xs: 11, sm: 12 }, 
                        color: "#fff",
                        fontWeight: "bold",
                        bgcolor: "#F02C56",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "12px",
                        display: "inline-block",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {video.talent}
                    </Typography>
                  </Box>
                </Box>

                {/* Video Section */}
                <Box
                  sx={{ 
                    position: "relative", 
                    display: "flex", 
                    justifyContent: "center"
                  }}
                >
                  <Box
                    ref={el => videoContainerRefs.current[video.id] = el}
                    sx={{
                      width: "100%",
                      borderRadius: 3,
                      overflow: "hidden",
                      background: "#000", // Changed to black background for letterboxing
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      position: "relative",
                      // CHANGE: Slightly reduced height to compact the layout
                      height: { xs: "240px", sm: "280px", md: "320px" },
                      transition: "all 0.3s ease"
                    }}
                  >
                  <video
        ref={el => videoRefs.current[video.id] = el}
        onTimeUpdate={() => handleTimeUpdate(video.id)}
        onLoadedMetadata={() => handleMetadataLoaded(video.id)}
        src={video.thumbnail}
        muted={videoStates[video.id]?.muted}
                      loop
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        // cursor: "pointer",
                      }}
                    />
                    
                    {/* Play Button - Styled like in Posts */}
                    {(!videoStates[video.id]?.playing) && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          borderRadius: "50%",
                          padding: 0,
                          cursor: "pointer",
                          animation: "pulse 1.8s infinite",
                          zIndex: 10, // Ensure it's above the video
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // handlePlayPause(video.id, e);
                          handleVideoClick(video.id)
                        }}
                      >
                        <AiOutlinePlayCircle size={40} color="#fff" />
                      </Box>
                    )}

                    {/* "Tap for sound" overlay - MODIFIED to show it now controls all videos */}
                    {(videoStates[video.id]?.playing && videoStates[video.id]?.muted) && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          borderRadius: 2,
                          padding: "4px 10px",
                          color: "white",
                          fontSize: { xs: 11, sm: 12 },
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          zIndex: 10,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.9)",
                            transform: "scale(1.05)",
                          },
                          animation: "pulse 2s infinite"
                        }}
                        onClick={(e) => toggleMute(video.id, e)}
                      >
                        <IoVolumeMute size={16} color="#fff" />
                        Tap for all sound
                      </Box>
                    )}

                    {/* Mute Button - MODIFIED tooltip text on hover */}
                    <Box
                      onClick={(e) => toggleMute(video.id, e)}
                      sx={{
                        position: "absolute",
                        bottom: 10,
                        left: 10,
                        width: { xs: 28, sm: 32 },
                        height: { xs: 28, sm: 32 },
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                        "&:hover::after": {
                          content: `"${videoStates[video.id]?.muted ? 'Unmute all' : 'Mute all'}"`,
                          position: "absolute",
                          bottom: "100%",
                          left: "50%",
                          transform: "translateX(-50%) translateY(-8px)",
                          backgroundColor: "rgba(0, 0, 0, 0.7)",
                          color: "#fff",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "10px",
                          whiteSpace: "nowrap",
                          pointerEvents: "none"
                        }
                      }}
                    >
                      {videoStates[video.id]?.muted ? 
                        <IoVolumeMute size={18} color="#fff" /> : 
                        <IoVolumeHigh size={18} color="#fff" />
                      }
                    </Box>

                    {/* Progress Bar */}
                    <Box
                      ref={el => progressBarRefs.current[video.id] = el}
                      onClick={(e) => handleProgressClick(e, video.id)}
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        height: { xs: 4, sm: 6 },
                        cursor: "pointer",
                        backgroundColor: "rgba(255,255,255,0.3)",
                        zIndex: 3,
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${videoStates[video.id]?.progress || 0}%`,
                          background: isOwnVideo(video) 
                            ? "linear-gradient(to right, #8B5CF6, #A78BFA)"
                            : "linear-gradient(to right, #F02C56, #FF6A88)",
                          transition: "width 0.2s ease",
                          borderRadius: "0 4px 4px 0",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>                
                
                {/* Description */}
                <Box sx={{ mt: 1.5, px: 0.5 }}> {/* Reduced margin */}
                  <Typography sx={{ 
                    fontSize: { xs: 14, sm: 15 }, 
                    color: "#333", 
                    fontWeight: 500,
                    mb: 0.5, // Reduced margin
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.4
                  }}>
                    {video.title}
                  </Typography>
                  
                  {/* Hashtags if available */}
                  {video.hashtags && video.hashtags.length > 0 && (
                    <Box 
                      sx={{ 
                        fontSize: { xs: 12, sm: 13 }, 
                        color: "#2196f3", 
                        mt: 0.5, 
                        mb: 0.5, // Reduced margin
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {video.hashtags.map((tag, i) => (
                        <span key={i} style={{ marginRight: 6, cursor: "pointer" }}>
                          {tag}
                        </span>
                      ))}
                    </Box>
                  )}
                  
                  {/* Created Date (small and subtle) */}
                  <Typography sx={{ 
                    fontSize: { xs: 10, sm: 11 }, 
                    color: "#888", 
                    mt: 0.5, // Reduced margin
                    fontStyle: "italic" 
                  }}>
                    Saved on: {new Date(video.createdAt).toLocaleDateString()}
                  </Typography>
                  
                  {/* Remove Button - Styled differently for own videos */}
                  <Box
  onClick={(e) => {
    e.stopPropagation();
    openDeleteDialog(video.id, e);
  }}
  sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    mt: 1.5,
    py: 0.75,
    px: { xs: 2, sm: 3 },
    borderRadius: 8,
    bgcolor: isOwnVideo(video) 
      ? "rgba(139, 92, 246, 0.1)"
      : "rgba(240, 44, 86, 0.1)",
    color: isOwnVideo(video) ? "#8B5CF6" : "#F02C56",
    cursor: "pointer",
    transition: "all 0.3s ease",
    "&:hover": {
      bgcolor: isOwnVideo(video) 
        ? "rgba(139, 92, 246, 0.2)"
        : "rgba(240, 44, 86, 0.2)",
      transform: "translateY(-2px)"
    }
  }}
>
  <BookmarkRemove fontSize="small" />
  <Typography sx={{ 
    fontSize: { xs: 12, sm: 13 }, 
    fontWeight: 500 
  }}>
    Remove from Saved
  </Typography>
</Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        
        {/* Visual feedback for global mute action */}
        {savedVideos.length > 0 && (
          <Box 
            sx={{ 
              position: "fixed",
              bottom: 20,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              color: "#fff",
              borderRadius: 4,
              py: 1,
              px: 3,
              display: "flex",
              alignItems: "center",
              gap: 1,
              zIndex: 1000,
              opacity: 0,
              pointerEvents: "none",
              transition: "opacity 0.3s ease",
              "&.show": {
                opacity: 1
              },
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
            }}
            className="global-mute-feedback"
            id="globalMuteFeedback"
          >
            {Object.keys(videoStates).length > 0 && 
              Object.values(videoStates)[0]?.muted ? (
              <>
                <IoVolumeMute size={18} />
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>All videos muted</Typography>
              </>
            ) : (
              <>
                <IoVolumeHigh size={18} />
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>All videos unmuted</Typography>
              </>
            )}
          </Box>
        )}
        
      </Box>
      <Dialog
  open={deleteDialogOpen}
  onClose={closeDeleteDialog}
  PaperProps={{
    sx: {
      borderRadius: 3,
      padding: 1,
      maxWidth: { xs: "90%", sm: "450px" }
    }
  }}
>
  <DialogTitle sx={{ 
    display: "flex", 
    alignItems: "center", 
    gap: 1.5,
    borderBottom: "1px solid rgba(0,0,0,0.1)",
    pb: 2
  }}>
    <Warning sx={{ color: "#F02C56" }} />
    <Typography sx={{ fontWeight: 600, fontSize: { xs: 18, sm: 20 } }}>
      Confirm Removal
    </Typography>
  </DialogTitle>
  
  <DialogContent sx={{ mt: 2, py: 1 }}>
    <Typography sx={{ fontSize: { xs: 14, sm: 16 }, color: "#555" }}>
      Are you sure you want to remove this video from your saved collection?
    </Typography>
    
    <Box sx={{ 
      mt: 3, 
      display: "flex", 
      alignItems: "center", 
      gap: 2, 
      padding: 2, 
      borderRadius: 2, 
      bgcolor: "rgba(240, 44, 86, 0.1)" 
    }}>
      <DeleteOutline sx={{ color: "#F02C56" }} />
      <Typography sx={{ 
        fontSize: { xs: 13, sm: 14 }, 
        color: "#333", 
        fontWeight: 500 
      }}>
        This video will be removed from your saved collection. You can always find and save it again later.
      </Typography>
    </Box>
  </DialogContent>
  
  <DialogActions sx={{ 
    display: "flex", 
    justifyContent: "space-between", 
    px: 3, 
    pb: 3, 
    pt: 1 
  }}>
    <Button
      onClick={closeDeleteDialog}
      sx={{
        px: 3,
        py: 1,
        borderRadius: 8,
        color: "#555",
        border: "1px solid rgba(0,0,0,0.15)",
        "&:hover": {
          backgroundColor: "rgba(0,0,0,0.05)"
        }
      }}
    >
      Cancel
    </Button>
    
    <Button
      onClick={confirmDelete}
      sx={{
        px: 3,
        py: 1,
        borderRadius: 8,
        bgcolor: "#F02C56",
        color: "white", 
        "&:hover": {
          bgcolor: "#d12a4f"
        }
      }}
    >
      Remove
    </Button>
  </DialogActions>
</Dialog>
    </Box>
  );
};

// Add a small utility function that shows the global mute feedback toast
// You should call this after toggling mute state
const showGlobalMuteFeedback = () => {
  const feedbackEl = document.getElementById('globalMuteFeedback');
  if (feedbackEl) {
    feedbackEl.classList.add('show');
    setTimeout(() => {
      feedbackEl.classList.remove('show');
    }, 2000); // Hide after 2 seconds
  }
};

export default SavedVideos;
